export interface Article {
  id: string;
  title: string;
  slug: string;
  categoryId: string;
  excerpt: string;
  content: string;
  coverImage: string;
  publishedAt: string;
  author: string;
  viewCount: number;
  tags: string[];
}

export const articles: Article[] = [
  {
    id: "a-1",
    title: "2024年内容创作趋势：从流量思维到价值思维的转变",
    slug: "content-creation-trends-2024",
    categoryId: "cat-3",
    excerpt: "随着互联网内容生态的不断演变，创作者们正经历着从追逐流量到深耕价值的重大转型。本文将深入探讨这一趋势背后的逻辑与机遇。",
    content: `<p>在数字化浪潮席卷全球的今天，内容创作领域正经历着一场深刻的变革。过去十年间，"流量为王"的理念主导着整个行业，创作者们不惜一切代价追求点击量和曝光度。然而，随着用户审美的提升和平台的算法升级，单纯依靠标题党和低质内容获取流量的方式已经难以为继。</p>
    <h2>流量思维的黄昏</h2>
    <p>回顾2020年至2023年，我们见证了无数靠博眼球内容迅速崛起又迅速陨落的案例。那些依靠夸张标题、虚假承诺吸引用户点击的内容，虽然在短期内获得了可观的流量数据，但用户的留存率和转化率却持续走低。平台的算法也在不断进化，越来越倾向于推荐真正有价值的内容。</p>
    <p>数据显示，2023年用户对内容质量的关注度提升了47%，而对标题吸引力的敏感度下降了23%。这意味着用户正在用脚投票，他们更愿意花时间阅读那些真正能带来认知提升或实用价值的内容。</p>
    <h2>价值思维的核心要素</h2>
    <p>价值思维并非简单地追求内容深度，而是在以下几个维度上实现平衡：</p>
    <p>第一，<strong>信息密度</strong>。优秀的内容应该在有限的篇幅内提供尽可能多的有效信息。这不仅要求创作者具备扎实的专业知识，还需要具备将复杂概念通俗化的能力。</p>
    <p>第二，<strong>情感共鸣</strong>。数据表明，能够引发用户情感共鸣的内容，其分享率是纯信息类内容的3.2倍。创作者需要学会在专业性和人情味之间找到平衡点。</p>
    <p>第三，<strong>实用价值</strong>。用户阅读内容的根本目的往往是解决问题或获得启发。那些能直接帮助用户解决实际问题的内容，天然具有更强的生命力。</p>
    <h2>未来展望</h2>
    <p>展望2024年及未来，我们认为内容创作将进入一个"精耕细作"的时代。创作者需要更加专注于自己擅长的垂直领域，通过持续输出高质量内容建立个人品牌和粉丝信任。短期来看，这可能需要更多的投入和耐心；但长期来看，这是构建可持续创作生涯的唯一正确路径。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20minimalist%20content%20creation%20workspace%20with%20laptop%20and%20notebook%20on%20clean%20wooden%20desk%2C%20warm%20ambient%20lighting%2C%20creative%20atmosphere%2C%20professional%20photography%2C%20soft%20shadows%2C%20editorial%20style%20with%20neutral%20cream%20and%20beige%20tones%2C%20decorative%20elements%20like%20a%20small%20plant%20and%20coffee%20cup%2C%20bright%20and%20airy%20feel&width=800&height=450&seq=article-cover-1&orientation=landscape",
    publishedAt: "2024-12-15",
    author: "内容观察员",
    viewCount: 12830,
    tags: ["内容创作", "趋势分析", "行业洞察"],
  },
  {
    id: "a-2",
    title: "高效阅读方法论：如何每天用30分钟获取一本书的精华",
    slug: "efficient-reading-methodology",
    categoryId: "cat-5",
    excerpt: "在信息爆炸的时代，掌握高效阅读方法比以往任何时候都重要。本文分享一套经过验证的30分钟快速阅读法，帮助你高效吸收知识。",
    content: `<p>现代人每天接收的信息量相当于15世纪一个人一生接收的信息总和。在这样的背景下，高效阅读已经成为一项必备的生存技能。但高效阅读不等于快速翻阅，而是一套系统的方法论。</p>
    <h2>为什么要学习高效阅读</h2>
    <p>根据2024年的一项调查，职场人士平均每周只有不到3小时的深度阅读时间。与此同时，知识更新的速度却在不断加快。如果不能在这有限的时间内有效吸收知识，就很容易在职业发展中落后。</p>
    <h2>30分钟阅读框架</h2>
    <p>这套框架将30分钟分为四个阶段：</p>
    <p><strong>第一阶段：结构预览（5分钟）</strong></p>
    <p>快速浏览目录、前言、结论和每章的小标题，建立对全书结构的整体认知。这一步的目的是回答三个问题：这本书在讲什么？它怎么讲的？它和我的需求匹配吗？</p>
    <p><strong>第二阶段：核心提取（15分钟）</strong></p>
    <p>重点关注每章的开头和结尾段落，以及加粗、列表、案例等关键内容。研究表明，一本书80%的核心观点集中在20%的篇幅中。学会识别这20%是关键。</p>
    <p><strong>第三阶段：笔记整理（7分钟）</strong></p>
    <p>用自己的语言记录3-5个核心观点，以及每个观点对应的一个具体行动方案。研究表明，主动输出的学习效果是被动输入的6倍。</p>
    <p><strong>第四阶段：关联思考（3分钟）</strong></p>
    <p>将新知识与已有知识体系建立连接。问自己：这个观点和我之前学到的什么内容有关？我能马上在哪里用到它？</p>
    <h2>常见误区</h2>
    <p>很多人在尝试高效阅读时容易陷入两个误区：一是追求速度而忽略了理解，二是不做笔记导致遗忘。记住，高效阅读的目标不是读完，而是学会。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Person%20reading%20a%20book%20in%20a%20cozy%20minimalist%20reading%20nook%20with%20warm%20natural%20light%20streaming%20through%20window%2C%20soft%20cream%20and%20beige%20interior%2C%20comfortable%20armchair%2C%20side%20table%20with%20coffee%2C%20peaceful%20atmosphere%2C%20editorial%20lifestyle%20photography%2C%20clean%20aesthetic&width=800&height=450&seq=article-cover-2&orientation=landscape",
    publishedAt: "2024-12-10",
    author: "阅读达人",
    viewCount: 9520,
    tags: ["阅读方法", "效率提升", "个人成长"],
  },
  {
    id: "a-3",
    title: "数字时代的深度思考：我们为何需要「离线时间」",
    slug: "deep-thinking-in-digital-age",
    categoryId: "cat-3",
    excerpt: "智能手机和社交媒体让我们的注意力被无限分割。本文探讨为什么定期「断联」对保持深度思考能力至关重要。",
    content: `<p>我们正生活在一个注意力被无限分割的时代。平均每个人每天拿起手机的次数超过150次，在社交媒体上花费超过2小时。这种持续的碎片化刺激正在悄悄侵蚀我们的深度思考能力。</p>
    <h2>注意力的危机</h2>
    <p>神经科学研究表明，当我们在不同任务之间频繁切换时，大脑需要大约23分钟才能重新进入深度专注状态。而我们大多数人的注意力集中时间已经被压缩到了平均只有47秒。</p>
    <p>更令人担忧的是，长期处于碎片化信息环境中，我们的大脑会逐渐丧失构建复杂思维框架的能力。这种现象被心理学家称为"认知浅薄化"。</p>
    <h2>离线时间的科学依据</h2>
    <p>多项研究表明，定期的"数字排毒"可以显著提升创造力和问题解决能力。一项针对知识工作者的研究发现，每周进行至少4小时无数字设备深度工作的人，其创新产出比对照组高出35%。</p>
    <h2>实践建议</h2>
    <p>建立离线时间不需要极端地抛弃所有数字设备，而是有意识地管理使用方式。建议从每天30分钟的完全离线开始，逐步延长。重要的是保持一致性，将其固化为日常习惯。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20person%20writing%20in%20journal%20by%20hand%2C%20phone%20placed%20face%20down%20on%20table%2C%20natural%20morning%20light%2C%20clean%20cream%20and%20warm%20beige%20aesthetic%2C%20peaceful%20mindfulness%20moment%2C%20editorial%20lifestyle%20photography%2C%20soft%20shadows%2C%20calm%20atmosphere&width=800&height=450&seq=article-cover-3&orientation=landscape",
    publishedAt: "2024-12-08",
    author: "深思者",
    viewCount: 7650,
    tags: ["深度思考", "数字健康", "生活方式"],
  },
  {
    id: "a-4",
    title: "从零开始学写作：新手必备的五个实用技巧",
    slug: "writing-tips-for-beginners",
    categoryId: "cat-4",
    excerpt: "写作是一项可以通过刻意练习掌握的能力。本文为写作新手总结了五个最实用的起步技巧，帮你快速跨越从0到1的门槛。",
    content: `<p>很多人认为写作需要天赋，但事实是，写作更像是一门手艺——它可以通过正确的方法和持续的练习来掌握。以下是五个经过验证的写作入门技巧。</p>
    <h2>技巧一：先写再改</h2>
    <p>写作最大的敌人是完美主义。许多人在写下第一句话之前就陷入自我怀疑。解决这个问题的方法很简单：先把所有想法倾倒出来，不要管语法、逻辑和措辞。初稿的唯一目的是存在。</p>
    <h2>技巧二：建立写作仪式感</h2>
    <p>固定的时间、固定的地点、固定的音乐——这些看似无关的细节能为大脑建立"进入写作状态"的条件反射。研究表明，建立仪式感的写作者，其创作效率平均提升40%。</p>
    <h2>技巧三：用对话的语言写作</h2>
    <p>最好的写作读起来就像作者在和你面对面聊天。避免使用过于复杂的词汇和冗长的句子。一个简单的测试方法：把你的文章读出声来，如果听起来不自然，那就需要修改。</p>
    <h2>技巧四：大量阅读</h2>
    <p>输出永远受限于输入。每天至少花30分钟阅读优质内容，不仅限于你擅长的领域。多样化的阅读能为你的写作提供更丰富的素材和视角。</p>
    <h2>技巧五：建立反馈循环</h2>
    <p>找人阅读你的作品并提供反馈是加速进步的最有效方式。不要害怕批评，把它视为成长的机会。一个成熟的写作者应该能够在批评中找到有价值的信息。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Elegant%20minimalist%20desk%20setup%20with%20open%20notebook%20and%20fountain%20pen%2C%20morning%20sunlight%20casting%20soft%20shadows%2C%20clean%20cream%20and%20warm%20neutral%20background%2C%20inspirational%20creative%20writing%20atmosphere%2C%20editorial%20photography%20style%2C%20high%20detail%2C%20calm%20and%20focused%20mood&width=800&height=450&seq=article-cover-4&orientation=landscape",
    publishedAt: "2024-12-05",
    author: "写作教练",
    viewCount: 11100,
    tags: ["写作技巧", "新手入门", "创作指南"],
  },
  {
    id: "a-5",
    title: "2024年度最佳读物推荐：十本改变思维的好书",
    slug: "best-books-2024-recommendation",
    categoryId: "cat-1",
    excerpt: "我们为你精选了2024年最值得阅读的十本书，涵盖心理学、商业、科技和人文领域，每本都经过读者口碑验证。",
    content: `<p>2024年出版了众多优秀书籍。我们的编辑团队从读者口碑、专业评价和内容质量三个维度，精心筛选出以下十本改变思维的年度好书。</p>
    <h2>心理学类</h2>
    <p>今年的心理学书籍更加注重实用性和可操作性，试图将学术研究成果转化为普通人可以日常运用的工具。</p>
    <p>推荐书目包括探讨习惯养成的《微习惯的力量》、研究决策机制的《选择的悖论》新版，以及关注数字时代心理健康的《屏幕之外》。</p>
    <h2>商业与创新</h2>
    <p>商业书籍的主题集中在AI时代的组织变革和个体竞争力重塑。重点推荐《人机协作新范式》和《小而美：独立创业者的生存指南》。</p>
    <h2>人文思考</h2>
    <p>在人文领域，关于意义感和幸福感的讨论持续升温。《慢下来》和《对话的艺术》是今年最受欢迎的两个人文话题作品。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Stack%20of%20beautiful%20books%20arranged%20on%20minimalist%20wooden%20shelf%20with%20warm%20reading%20lamp%20glow%2C%20cozy%20library%20corner%2C%20cream%20and%20beige%20interior%20with%20textured%20throw%20blanket%2C%20editorial%20book%20photography%2C%20soft%20natural%20light%2C%20scholarly%20yet%20inviting%20atmosphere&width=800&height=450&seq=article-cover-5&orientation=landscape",
    publishedAt: "2024-12-01",
    author: "读书编辑",
    viewCount: 15600,
    tags: ["书单推荐", "阅读指南", "年度精选"],
  },
  {
    id: "a-6",
    title: "社交媒体运营实战指南：从0到10万粉丝的增长方法论",
    slug: "social-media-growth-guide",
    categoryId: "cat-5",
    excerpt: "如何在激烈的社交媒体竞争中脱颖而出？本文总结了多个平台的实战经验，提供一套可复制的粉丝增长策略。",
    content: `<p>社交媒体运营已经成为个人品牌建设和商业推广的核心渠道。然而，大多数人在运营过程中面临的最大挑战不是缺乏创意，而是缺乏系统性的方法论。</p>
    <h2>内容策略三层模型</h2>
    <p>成功的社交媒体账号通常遵循三层内容策略：引流层（40%）、价值层（40%）和转化层（20%）。引流层的内容负责吸引新用户，价值层的内容负责建立信任，转化层的内容负责实现商业目标。</p>
    <h2>数据驱动的优化方法</h2>
    <p>每周花30分钟分析数据，关注完播率、互动率和分享率三个核心指标。根据数据反馈持续优化内容形式、发布时间和话题选择。数据显示，坚持数据驱动优化的账号，其增长速度比随意运营的账号快2.5倍。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20social%20media%20analytics%20dashboard%20displayed%20on%20laptop%20screen%2C%20minimalist%20desk%20setup%20with%20phone%20and%20notebook%2C%20warm%20ambient%20office%20lighting%2C%20clean%20cream%20and%20neutral%20aesthetic%2C%20professional%20creative%20workspace%2C%20editorial%20tech%20photography&width=800&height=450&seq=article-cover-6&orientation=landscape",
    publishedAt: "2024-11-28",
    author: "运营实战派",
    viewCount: 8840,
    tags: ["社交媒体", "运营技巧", "粉丝增长"],
  },
  {
    id: "a-7",
    title: "远程办公两年后，我总结的10条效率提升经验",
    slug: "remote-work-efficiency-tips",
    categoryId: "cat-4",
    excerpt: "远程办公已经成为许多人的工作常态。本文作者分享了过去两年远程办公中总结的10条实用效率提升经验。",
    content: `<p>距离大规模远程办公浪潮已经过去两年。在这两年中，我尝试了各种方法优化自己的工作流程。以下是经过验证的10条效率提升经验。</p>
    <h2>工作环境篇</h2>
    <p>最重要的经验是：工作和生活空间必须物理分离。即使住在小公寓，也要通过屏风或家具布局划分出专属工作区。心理学的"情境效应"表明，固定的工作环境能显著提升大脑的专注状态切换速度。</p>
    <h2>时间管理篇</h2>
    <p>采用"90分钟专注块"模式代替传统的番茄钟。研究发现，知识工作者进入深度工作状态需要约15分钟，90分钟是维持高质量输出的最佳时长。每天安排2-3个这样的专注块即可。</p>
    <h2>沟通协作篇</h2>
    <p>过度沟通和沟通不足同样有害。建立清晰的异步沟通规范，区分紧急和非紧急事务的沟通渠道。非紧急事务留给对方24小时回复时间。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Cozy%20home%20office%20setup%20with%20laptop%20and%20plant%20by%20large%20window%2C%20natural%20sunlight%2C%20minimalist%20cream%20and%20beige%20interior%2C%20comfortable%20ergonomic%20chair%2C%20clean%20and%20organized%20workspace%2C%20editorial%20lifestyle%20photography%2C%20warm%20and%20inviting%20atmosphere&width=800&height=450&seq=article-cover-7&orientation=landscape",
    publishedAt: "2024-11-25",
    author: "远程工作者",
    viewCount: 10320,
    tags: ["远程办公", "效率提升", "工作方式"],
  },
  {
    id: "a-8",
    title: "AI辅助创作实战：如何用AI工具提升内容产出3倍",
    slug: "ai-assisted-content-creation",
    categoryId: "cat-2",
    excerpt: "AI工具正在改变内容创作的方式。本文分享如何正确使用AI辅助创作，在保持内容质量的同时大幅提升产出效率。",
    content: `<p>人工智能正在深刻地改变内容创作的格局。但AI不是取代创作者的工具，而是放大创作者能力的利器。正确使用AI辅助创作，可以在不牺牲质量的前提下将产出效率提升3倍以上。</p>
    <h2>AI在创作中的正确定位</h2>
    <p>将AI视为创意伙伴而非替代品。AI擅长的是信息整理、初稿生成和多角度思考，而人类创作者的核心价值在于独特视角、情感表达和质量把控。将两者结合，才能发挥最大效能。</p>
    <h2>实用工作流程</h2>
    <p>推荐的AI辅助创作流程：头脑风暴阶段用AI生成多种思路，大纲阶段用AI检视结构完整性，初稿阶段用AI快速生成框架，修改阶段保持人类主导。整个过程中，人类创作者始终是最终决策者。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Futuristic%20yet%20minimalist%20creative%20workspace%20with%20laptop%20displaying%20AI%20interface%2C%20warm%20ambient%20lighting%2C%20clean%20cream%20and%20beige%20aesthetic%2C%20notepad%20and%20pen%20beside%20computer%2C%20editorial%20tech%20photography%2C%20thoughtful%20composition%2C%20soft%20shadows&width=800&height=450&seq=article-cover-8&orientation=landscape",
    publishedAt: "2024-11-20",
    author: "科技观察者",
    viewCount: 13700,
    tags: ["AI工具", "内容创作", "效率提升"],
  },
  {
    id: "a-9",
    title: "个人品牌打造全攻略：从定位到变现的完整路径",
    slug: "personal-brand-building-guide",
    categoryId: "cat-5",
    excerpt: "在注意力经济时代，个人品牌是最有价值的资产之一。本文提供一套从定位、内容到变现的完整个人品牌打造路径。",
    content: `<p>个人品牌不再只是名人和意见领袖的专属。在数字经济时代，每个人都应该经营自己的个人品牌。它是在职场和商业中建立信任、获取机会的最有效方式。</p>
    <h2>定位：找到你的独特价值点</h2>
    <p>好的定位需要回答三个问题：你擅长什么？市场需要什么？你热爱什么？三个问题的交集就是你理想的定位方向。不要试图面面俱到，精准的定位比宽泛的覆盖面更有力量。</p>
    <h2>内容：持续输出建立信任</h2>
    <p>内容是个人品牌的载体。制定可持续的内容策略比追求爆款更重要。每周稳定输出2-3篇高质量内容，持续6个月以上，你会看到质变。</p>
    <h2>变现：信任的自然结果</h2>
    <p>当你的个人品牌积累了足够的信任，变现就是水到渠成的事。常见的变现方式包括咨询服务、在线课程、付费社群和品牌合作。关键是选择与你定位一致的变现方式。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Professional%20person%20working%20on%20branding%20strategy%20sketches%20on%20whiteboard%2C%20modern%20minimalist%20office%20with%20warm%20lighting%2C%20cream%20and%20beige%20interior%20design%2C%20creative%20atmosphere%2C%20editorial%20business%20photography%2C%20clean%20professional%20look&width=800&height=450&seq=article-cover-9&orientation=landscape",
    publishedAt: "2024-11-18",
    author: "品牌顾问",
    viewCount: 9160,
    tags: ["个人品牌", "职业发展", "商业思维"],
  },
  {
    id: "a-10",
    title: "碎片化学习不靠谱？科学研究告诉你真相",
    slug: "microlearning-effectiveness-research",
    categoryId: "cat-3",
    excerpt: "碎片化学习被很多人质疑效果，但最新的认知科学研究表明，关键不在于学习时间的长短，而在于学习方法。",
    content: `<p>"碎片化学习只是自我安慰"、"一天学5分钟能有什么用"——这些质疑你可能听过很多次。但最新的认知科学研究提供了不同的视角。</p>
    <h2>间隔效应的力量</h2>
    <p>认知心理学中著名的"间隔效应"表明，将学习内容分散在多个时间段中，比集中学习更有利于长期记忆。碎片化学习天然符合这一原理，前提是每次学习都有清晰的微目标。</p>
    <h2>有效碎片化学习的三个原则</h2>
    <p>第一，每次学习聚焦一个微概念，确保完全理解后再进入下一个。第二，在每次学习之间进行主动回忆，而不是被动重读。第三，将新知识与已有知识建立联系，形成知识网络。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Person%20using%20phone%20for%20learning%20in%20a%20bright%20minimalist%20cafe%2C%20warm%20natural%20light%2C%20cream%20and%20beige%20aesthetic%2C%20notepad%20with%20notes%20on%20table%2C%20editorial%20lifestyle%20photography%2C%20modern%20casual%20atmosphere%2C%20clean%20composition&width=800&height=450&seq=article-cover-10&orientation=landscape",
    publishedAt: "2024-11-15",
    author: "学习研究者",
    viewCount: 7850,
    tags: ["学习方法", "科学研究", "认知心理"],
  },
  {
    id: "a-11",
    title: "内容变现新思路：2024年创作者经济五大趋势",
    slug: "creator-economy-trends-2024",
    categoryId: "cat-2",
    excerpt: "创作者经济正在进入2.0时代。本文梳理了2024年创作者变现的五大新趋势，帮助你把握内容商业化的最新机会。",
    content: `<p>创作者经济经历过狂热和冷静，正在进入一个更加成熟的发展阶段。2024年，我们观察到五个值得关注的新趋势。</p>
    <h2>趋势一：从广告到直接付费</h2>
    <p>越来越多的创作者正在减少对广告收入的依赖，转而建立直接的付费关系。订阅制、付费社群、一对一咨询等模式正在成为主流。</p>
    <h2>趋势二：垂直化深耕</h2>
    <p>泛内容领域的竞争已经白热化，而垂直细分领域仍然存在大量机会。尤其在教育、健康和专业服务等领域，深耕垂直赛道的创作者获得了更高的用户粘性和付费意愿。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20with%20multiple%20screens%20showing%20analytics%20and%20content%20dashboards%2C%20warm%20lighting%2C%20minimalist%20cream%20and%20beige%20interior%2C%20professional%20creative%20atmosphere%2C%20editorial%20workspace%20photography%2C%20clean%20tech%20aesthetic&width=800&height=450&seq=article-cover-11&orientation=landscape",
    publishedAt: "2024-11-12",
    author: "商业分析师",
    viewCount: 11800,
    tags: ["创作者经济", "内容变现", "商业趋势"],
  },
  {
    id: "a-12",
    title: "健康使用手机：七个实用习惯改变你的数字生活",
    slug: "healthy-phone-habits",
    categoryId: "cat-4",
    excerpt: "手机已经成为我们身体的一部分，但过度使用带来的负面影响不容忽视。本文分享七个实用习惯，帮你建立健康的数字生活方式。",
    content: `<p>平均每人每天查看手机超过150次。虽然手机极大地便利了我们的生活，但不健康的手机使用习惯正在损害我们的注意力、睡眠质量和人际关系。</p>
    <h2>习惯一：起床后30分钟不看手机</h2>
    <p>研究表明，早晨第一件事就是查看手机会激活大脑的压力反应，影响一整天的情绪状态。把这30分钟留给伸展、冥想或阅读纸质书。</p>
    <h2>习惯二：设立无手机区域</h2>
    <p>餐桌和卧室是两个最重要的无手机区域。在餐桌上放下手机能改善家庭关系；在卧室不用手机能显著提升睡眠质量。</p>`,
    coverImage: "https://readdy.ai/api/search-image?query=Phone%20placed%20on%20wooden%20table%20beside%20a%20cup%20of%20tea%2C%20person%20reading%20physical%20book%20in%20background%2C%20warm%20morning%20sunlight%20through%20window%2C%20minimalist%20cream%20and%20beige%20interior%2C%20peaceful%20mindful%20moment%2C%20editorial%20lifestyle%20photography&width=800&height=450&seq=article-cover-12&orientation=landscape",
    publishedAt: "2024-11-10",
    author: "数字生活倡导者",
    viewCount: 10250,
    tags: ["数字健康", "生活习惯", "手机管理"],
  },
];

export const articlesBySlug: Record<string, Article> = {};
articles.forEach((a) => {
  articlesBySlug[a.slug] = a;
});